/**
 * Assignment rules — the single source of truth for "is this crew legal?".
 *
 * Everything that decides how a crew is shaped lives here: how a form draft
 * becomes a stored {@link TaskTeam}, which crew rows a team materialises, and
 * every way a crew can break the one rule that matters — a driver, an escort
 * or a passenger belongs to exactly one vehicle team, on exactly one vehicle.
 *
 * The store (`src/db/assignment-store.ts`) and the assignment UI
 * (`src/modules/tasks/assignment/`) both call in here; neither re-implements a
 * rule of its own.
 */
import {
  DEFAULT_ESCORT_OPTION,
  ASSIGNMENT_CONFLICT_LABELS,
  OVERRIDE_REASON_LABELS,
  OVERRIDE_REASON_REQUIRING_TEXT,
} from '../types/assignment';
import type {
  AssignmentConflict,
  AssignmentConflictCode,
  AssignmentOverride,
  CrewRole,
  ExternalPerson,
  ExternalPersonDraft,
  TaskCrewMember,
  TaskCrewMemberDraft,
  TaskTeam,
  TaskTeamDraft,
} from '../types/assignment';

const trimmed = (value: string | null | undefined): string => String(value ?? '').trim();

/** Optional free-text field — an empty string means "not set". */
const optional = (value: string | null | undefined): string | undefined => trimmed(value) || undefined;

export const newTeamId = (): string => `team-${crypto.randomUUID()}`;

export const newCrewId = (): string => `crew-${crypto.randomUUID()}`;

export const newExternalPassengerId = (): string => `ext-${crypto.randomUUID()}`;

/** Lookup by id over any of the domain lists, for the UI's name resolution. */
export function byId<T extends { id: string }>(
  items: readonly T[],
  id: string | null | undefined,
): T | null {
  if (!id) return null;
  return items.find((item) => item.id === id) ?? null;
}

// ── Normalisation ────────────────────────────────────────────────────

/**
 * One person off the roster, cleaned up. The temporary vehicle they arrive in
 * rides on the person — it is never a fleet record — and `directoryId` is kept
 * so a person taken from אנשי חוץ can be written back to the same card.
 */
export function normalizeExternalPerson(
  draft: ExternalPersonDraft | undefined,
): ExternalPerson | undefined {
  if (!draft) return undefined;
  return {
    id: draft.id ?? newExternalPassengerId(),
    fullName: trimmed(draft.fullName),
    personalNumber: optional(draft.personalNumber),
    role: optional(draft.role),
    phone: optional(draft.phone),
    notes: optional(draft.notes),
    vehicleModel: optional(draft.vehicleModel),
    plateNumber: optional(draft.plateNumber),
    directoryId: optional(draft.directoryId),
  };
}

/** The name this helper was born under, when only a passenger could be external. */
const normalizeExternalPassenger = normalizeExternalPerson;

/**
 * A passenger row, bound to its team. `teamId` is re-stamped from the owning
 * team on every pass, so a row can never drift away from its vehicle, and a
 * roster soldier never also carries manual passenger details.
 */
function normalizePassenger(draft: TaskCrewMemberDraft, teamId: string): TaskCrewMember {
  const soldierId = optional(draft.soldierId);
  return {
    id: draft.id ?? newCrewId(),
    teamId,
    soldierId,
    externalPassenger: soldierId ? undefined : normalizeExternalPassenger(draft.externalPassenger),
    crewRole: 'passenger',
  };
}

export interface NormalizeContext {
  taskId: string;
  /** The stored team being replaced, when there is one — it keeps `createdAt`. */
  existing?: TaskTeam | null;
  now?: number;
}

/** One form draft, cleaned up into the record the store persists. */
export function normalizeTeam(draft: TaskTeamDraft, context: NormalizeContext): TaskTeam {
  const stamp = new Date(context.now ?? Date.now()).toISOString();
  const id = draft.id ?? newTeamId();
  const escortOption = draft.escortOption ?? DEFAULT_ESCORT_OPTION;
  const driverId = optional(draft.driverId);
  return {
    id,
    taskId: context.taskId,
    vehicleId: trimmed(draft.vehicleId),
    driverId,
    // A roster driver and an external one are the same seat: naming a soldier
    // clears whoever was typed in, so the wheel is never held by two people.
    externalDriver: driverId ? undefined : normalizeExternalPerson(draft.externalDriver),
    escortOption,
    // "ללא מפקד משימה" and "טרם נקבע" can never leave a stale name behind.
    escortId: escortOption === 'soldier' ? optional(draft.escortId) : undefined,
    externalEscort:
      escortOption === 'external' ? normalizeExternalPerson(draft.externalEscort) : undefined,
    passengers: (draft.passengers ?? []).map((passenger) => normalizePassenger(passenger, id)),
    teamNote: optional(draft.teamNote),
    // A team with no warning overridden carries no metadata at all.
    overrides: draft.overrides?.length ? draft.overrides.map(normalizeOverride) : undefined,
    createdAt: context.existing?.createdAt ?? stamp,
    updatedAt: stamp,
  };
}

/** An override is stored as given — only its free text is tidied. */
function normalizeOverride(override: AssignmentOverride): AssignmentOverride {
  return { ...override, reasonText: optional(override.reasonText) };
}

/**
 * The whole crew of one task, cleaned up in one pass. Every team saved
 * together shares one stamp, so a convoy written in a single call never comes
 * back in a different order than it went in.
 */
export function normalizeTeams(
  drafts: readonly TaskTeamDraft[],
  taskId: string,
  existing: readonly TaskTeam[] = [],
  now: number = Date.now(),
): TaskTeam[] {
  return drafts.map((draft) =>
    normalizeTeam(draft, { taskId, existing: byId(existing, draft.id), now }),
  );
}

// ── Crew rows ────────────────────────────────────────────────────────

/**
 * Every person on a team as a crew row — the driver and the escort included,
 * so "who rides on this vehicle" is answerable from one table. The driver and
 * escort rows carry a derived id, so re-saving a team replaces them instead of
 * breeding duplicates.
 */
export function teamCrewMembers(team: TaskTeam): TaskCrewMember[] {
  const members: TaskCrewMember[] = [];
  if (team.driverId) {
    members.push({
      id: `${team.id}:driver`,
      teamId: team.id,
      soldierId: team.driverId,
      crewRole: 'driver',
    });
  } else if (team.externalDriver) {
    // Same seat, same derived id — an external driver is a crew row like any
    // other, so the headcount, the sheet and the order all see them.
    members.push({
      id: `${team.id}:driver`,
      teamId: team.id,
      externalPassenger: team.externalDriver,
      crewRole: 'driver',
    });
  }
  if (team.escortOption === 'soldier' && team.escortId) {
    members.push({
      id: `${team.id}:escort`,
      teamId: team.id,
      soldierId: team.escortId,
      crewRole: 'escort',
    });
  } else if (team.escortOption === 'external' && team.externalEscort) {
    members.push({
      id: `${team.id}:escort`,
      teamId: team.id,
      externalPassenger: team.externalEscort,
      crewRole: 'escort',
    });
  }
  for (const passenger of team.passengers) {
    members.push({ ...passenger, teamId: team.id });
  }
  return members;
}

/** One external person on a team, with the seat they were placed in. */
export interface TeamExternalPerson {
  crewRole: CrewRole;
  person: ExternalPerson;
}

/**
 * Everyone on this team who is not on the roster, driver and commander
 * included — what a card, an order and the image all read to mark them.
 */
export function teamExternalPeople(team: TaskTeam): TeamExternalPerson[] {
  return teamCrewMembers(team).flatMap((member) =>
    member.externalPassenger
      ? [{ crewRole: member.crewRole, person: member.externalPassenger }]
      : [],
  );
}

/** The temporary vehicles this team's external people arrive in. */
export function teamTemporaryVehicles(team: TaskTeam): ExternalPerson[] {
  return teamExternalPeople(team)
    .map((entry) => entry.person)
    .filter((person) => Boolean(trimmed(person.vehicleModel) || trimmed(person.plateNumber)));
}

/** Roster soldiers bound to one team, in any role. */
export function teamSoldierIds(team: TaskTeam): string[] {
  const ids: string[] = [];
  if (team.driverId) ids.push(team.driverId);
  if (team.escortOption === 'soldier' && team.escortId) ids.push(team.escortId);
  for (const passenger of team.passengers) {
    if (passenger.soldierId) ids.push(passenger.soldierId);
  }
  return ids;
}

/** How many people ride on this vehicle, roster and external alike. */
export function teamHeadcount(team: TaskTeam): number {
  return teamCrewMembers(team).length;
}

/**
 * Every soldier already taken by the task's other vehicles — what the team
 * form greys out, so a soldier is never offered twice.
 */
export function assignedSoldierIds(
  teams: readonly TaskTeam[],
  exceptTeamId?: string,
): Set<string> {
  const taken = new Set<string>();
  for (const team of teams) {
    if (team.id === exceptTeamId) continue;
    for (const soldierId of teamSoldierIds(team)) taken.add(soldierId);
  }
  return taken;
}

// ── Integrity ────────────────────────────────────────────────────────

const conflict = (code: AssignmentConflictCode, teamId: string): AssignmentConflict => ({
  code,
  teamId,
  message: ASSIGNMENT_CONFLICT_LABELS[code],
});

/**
 * Checks the crew of one task as a whole. Everything here is a binding
 * question: is the crew attached to a vehicle, is that vehicle used once, and
 * does every person ride on exactly one of them.
 */
export function validateTaskTeams(teams: readonly TaskTeam[]): AssignmentConflict[] {
  const conflicts: AssignmentConflict[] = [];
  const seenVehicles = new Set<string>();
  const seenSoldiers = new Set<string>();

  for (const team of teams) {
    if (!team.vehicleId) conflicts.push(conflict('MISSING_VEHICLE', team.id));
    else if (seenVehicles.has(team.vehicleId)) conflicts.push(conflict('DUPLICATE_VEHICLE', team.id));
    else seenVehicles.add(team.vehicleId);

    if (team.escortOption === 'soldier' && !team.escortId) {
      conflicts.push(conflict('ESCORT_NOT_CHOSEN', team.id));
    }

    // An external seat is only filled once it carries a name — the one field
    // the form makes mandatory, and the only one an order cannot be read without.
    if (team.escortOption === 'external' && !trimmed(team.externalEscort?.fullName)) {
      conflicts.push(conflict('EXTERNAL_PERSON_UNNAMED', team.id));
    }
    if (team.externalDriver && !trimmed(team.externalDriver.fullName)) {
      conflicts.push(conflict('EXTERNAL_PERSON_UNNAMED', team.id));
    }

    for (const soldierId of teamSoldierIds(team)) {
      if (seenSoldiers.has(soldierId)) conflicts.push(conflict('SOLDIER_IN_TWO_ROLES', team.id));
      else seenSoldiers.add(soldierId);
    }

    for (const passenger of team.passengers) {
      if (!passenger.soldierId && !passenger.externalPassenger) {
        conflicts.push(conflict('EMPTY_CREW_MEMBER', team.id));
      } else if (passenger.externalPassenger && !trimmed(passenger.externalPassenger.fullName)) {
        conflicts.push(conflict('EXTERNAL_PASSENGER_UNNAMED', team.id));
      }
    }

    // An override is a signed decision: without a known reason, a manager and
    // an explanation for "אחרת", it is not one.
    for (const override of team.overrides ?? []) {
      const unexplained =
        override.reason === OVERRIDE_REASON_REQUIRING_TEXT && !trimmed(override.reasonText);
      if (!OVERRIDE_REASON_LABELS[override.reason] || !override.managerId || unexplained) {
        conflicts.push(conflict('OVERRIDE_WITHOUT_REASON', team.id));
      }
    }
  }

  return conflicts;
}

/** Throwing variant, for guarding a write. */
export function assertTaskTeams(teams: readonly TaskTeam[]): void {
  const conflicts = validateTaskTeams(teams);
  if (conflicts.length === 0) return;
  throw new Error(
    `Assignment conflict — ${conflicts.map((item) => `${item.code}: ${item.message}`).join(' | ')}`,
  );
}

// ── List helpers ─────────────────────────────────────────────────────

/** Adds a team, or replaces the one that carries the same id. */
export function upsertTeam(teams: readonly TaskTeam[], team: TaskTeam): TaskTeam[] {
  const known = teams.some((current) => current.id === team.id);
  if (!known) return [...teams, team];
  return teams.map((current) => (current.id === team.id ? team : current));
}

export function removeTeam(teams: readonly TaskTeam[], teamId: string): TaskTeam[] {
  return teams.filter((current) => current.id !== teamId);
}
